/**
 * PaymentSuccessPage - Shows after successful Stripe payment
 * Polls payment status and updates UI accordingly
 */
import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { CheckCircle, Loader2, AlertCircle, ArrowRight, Calendar, Receipt } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PaymentSuccessPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [status, setStatus] = useState('checking'); // checking, success, failed
  const [paymentData, setPaymentData] = useState(null);
  const [attempts, setAttempts] = useState(0);
  
  const sessionId = searchParams.get('session_id');
  const maxAttempts = 10;
  const pollInterval = 2000;

  // Poll payment status
  useEffect(() => {
    if (!sessionId) {
      setStatus('failed');
      return;
    }

    const checkStatus = async () => {
      try {
        const response = await fetch(`${API_URL}/api/payments/event-deposit/status/${sessionId}`);
        
        if (!response.ok) throw new Error('Failed to check status');
        
        const data = await response.json();
        setPaymentData(data);
        
        if (data.payment_status === 'paid') {
          setStatus('success');
          toast.success('Payment confirmed!');
          return true;
        } else if (data.status === 'expired') {
          setStatus('failed');
          return true;
        }
        
        return false;
      } catch (error) {
        console.error('Status check error:', error);
        return false;
      }
    };

    const poll = async () => {
      const isDone = await checkStatus();
      if (!isDone && attempts < maxAttempts) {
        setTimeout(() => setAttempts(a => a + 1), pollInterval);
      } else if (!isDone) {
        // Max attempts reached
        setStatus('pending');
      }
    };

    poll();
  }, [sessionId, attempts]);

  if (status === 'checking') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="max-w-md w-full mx-4">
          <CardContent className="pt-8 text-center">
            <Loader2 className="w-16 h-16 animate-spin text-purple-600 mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2">Verifying Payment</h2>
            <p className="text-gray-500">Please wait while we confirm your payment...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-green-50 to-white flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-8 text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-12 h-12 text-green-600" />
            </div>
            
            <h1 className="text-2xl font-bold text-gray-800 mb-2">Payment Successful!</h1>
            <p className="text-gray-600 mb-6">
              Your deposit has been received. You'll get a confirmation email shortly.
            </p>

            {paymentData && (
              <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Amount Paid</span>
                    <span className="font-bold text-green-600">
                      {paymentData.currency?.toUpperCase()} {paymentData.amount?.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Booking ID</span>
                    <span className="font-mono text-xs">{paymentData.booking_id?.slice(0, 8)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Status</span>
                    <span className="text-green-600 font-medium">Confirmed</span>
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-3">
              <Button 
                className="w-full bg-green-600 hover:bg-green-700"
                onClick={() => navigate('/events')}
              >
                <Calendar className="w-4 h-4 mr-2" />
                View My Bookings
              </Button>
              <Button 
                variant="outline"
                className="w-full"
                onClick={() => navigate('/events')}
              >
                Browse More Services
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Failed or pending state
  return (
    <div className="min-h-screen bg-gradient-to-b from-red-50 to-white flex items-center justify-center p-4">
      <Card className="max-w-md w-full">
        <CardContent className="pt-8 text-center">
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <AlertCircle className="w-12 h-12 text-red-600" />
          </div>
          
          <h1 className="text-2xl font-bold text-gray-800 mb-2">
            {status === 'pending' ? 'Payment Pending' : 'Payment Issue'}
          </h1>
          <p className="text-gray-600 mb-6">
            {status === 'pending' 
              ? 'Your payment is still being processed. Please check back shortly or contact support.'
              : 'There was an issue with your payment. Please try again or contact support.'}
          </p>

          <div className="space-y-3">
            <Button 
              className="w-full"
              onClick={() => navigate('/events')}
            >
              Return to Events
            </Button>
            <Button 
              variant="outline"
              className="w-full"
              onClick={() => window.location.reload()}
            >
              Check Again
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
